import { router } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { BrandChip } from "@/components/creator-leaderboard/brand-chip"
import { GapDivider } from "@/components/creator-leaderboard/gap-divider"
import { LeaderboardEmpty } from "@/components/creator-leaderboard/leaderboard-empty"
import { LeaderboardPodium } from "@/components/creator-leaderboard/leaderboard-podium"
import { LeaderboardRow } from "@/components/creator-leaderboard/leaderboard-row"
import { LeaderboardSkeleton } from "@/components/creator-leaderboard/leaderboard-skeleton"
import { YouClimbedBanner } from "@/components/creator-leaderboard/you-climbed-banner"
import type { CreatorLeaderboardProps } from "@/types/creator-leaderboard"

/**
 * OKS-519 — §13 creator leaderboard composition (the 5 states).
 *
 *   - Loading — `<LeaderboardSkeleton>` while a full Inertia visit to
 *     this same surface is in flight (e.g. a brand switch).
 *   - Empty   — `empty_kind` set → `<LeaderboardEmpty>` ("crew getting
 *     started · Find a mission").
 *   - Error   — handled upstream by the shared Inertia error boundary.
 *   - Success — brand chip → climbed banner → podium → around-you window.
 *   - Edge    — <3 ranked → no podium, flat list (`podium` is `[]`).
 *
 * Server-driven: no period URL state, no tabs, no `localStorage`. A brand
 * switch is a full Inertia visit that re-renders this page for the new
 * brand (PRD AC §10).
 */
export function CreatorLeaderboardView({
  brand_name,
  brand_logo_url,
  crew_count,
  podium,
  around,
  gap_count,
  climb,
  viewer_membership_id,
  empty_kind,
}: CreatorLeaderboardProps) {
  const { t } = useTranslation()
  const loading = useLeaderboardVisitInFlight()

  if (loading) return <LeaderboardSkeleton />

  return (
    <div className="flex w-full flex-col gap-4">
      <BrandChip brandName={brand_name} brandLogoUrl={brand_logo_url} />

      <header className="flex flex-col gap-0.5">
        <h1 className="text-2xl font-bold">
          {t("leaderboard.creator.heading")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t("leaderboard.creator.subtitle", { count: crew_count })}
        </p>
      </header>

      {empty_kind ? (
        <LeaderboardEmpty />
      ) : (
        <>
          {climb && <YouClimbedBanner climb={climb} />}

          <LeaderboardPodium
            podium={podium}
            viewerMembershipId={viewer_membership_id}
          />

          {gap_count > 0 && <GapDivider count={gap_count} />}

          <ul
            data-testid="creator-leaderboard-list"
            aria-label={t("leaderboard.list.aria_label")}
            className="flex flex-col gap-1"
          >
            {around.map((row) => (
              <LeaderboardRow
                key={row.membership_id}
                row={row}
                viewerHighlight={row.membership_id === viewer_membership_id}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * Flips `true` while a full Inertia visit that lands back on THIS surface
 * is in flight (same pathname → a brand switch / reload), so the skeleton
 * shows without flashing on ordinary navigations away from the page.
 */
function useLeaderboardVisitInFlight() {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const stopStart = router.on("start", (event) => {
      const target = (event.detail.visit as { url?: URL }).url
      if (target?.pathname === window.location.pathname) {
        setLoading(true)
      }
    })
    const stopFinish = router.on("finish", () => setLoading(false))

    return () => {
      stopStart()
      stopFinish()
    }
  }, [])

  return loading
}
